import type { Observable } from 'rxjs';

/**
 * The subset of cookie consent that can be restricted by a platform-level
 * tracking authorization (e.g. iOS App Tracking Transparency). Functional
 * cookies are never gated and therefore not represented here.
 */
export interface TrackingConsent {
	analytics: boolean;
	marketing: boolean;
}

export interface ResolveTrackingOptions {
	/**
	 * When `true`, the gate is allowed to display a native permission prompt
	 * (e.g. the iOS ATT dialog). Use for explicit user actions such as saving
	 * cookie preferences. When `false` (default), the gate must resolve
	 * silently from the already-known authorization status.
	 */
	prompt?: boolean;
}

/**
 * Optional hook that lets a host application restrict analytics/marketing
 * consent based on a platform-level tracking authorization such as iOS App
 * Tracking Transparency.
 *
 * The shared cookie banner injects this abstraction as `optional`. When no
 * implementation is provided (all web apps) the banner applies no restriction
 * and behaves exactly as before. Only the native guest app provides an
 * ATT-backed implementation, so tracking cookies never fire on iOS unless the
 * user has authorized tracking.
 */
export abstract class TrackingConsentGate {
	/**
	 * Resolve the effective tracking consent for the requested preferences.
	 * Implementations may reduce the requested consent (e.g. force both flags
	 * to `false` when tracking is not authorized) but must never widen it.
	 */
	abstract resolve(
		requested: TrackingConsent,
		options?: ResolveTrackingOptions,
	): Observable<TrackingConsent>;
}
